import React, { useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, Trash2, Save, X } from "lucide-react";

const input = "w-full bg-[#0e0e0e] border border-white/15 px-3 py-2.5 text-sm text-white focus:border-[#D4FF00] focus:outline-none";
const label = "font-mono-t text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5 block";

function emptyFromFields(fields) {
  const o = {};
  fields.forEach((f) => (o[f.key] = f.type === "number" ? 0 : ""));
  return o;
}

export function CollectionEditor({ title, name, items, fields, refresh }) {
  const [editing, setEditing] = useState(null); // item being edited (or new)
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const openNew = () => {
    setForm({ ...emptyFromFields(fields), order: (items?.length || 0) + 1 });
    setEditing("new");
  };
  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item.id);
  };
  const close = () => {
    setEditing(null);
    setForm({});
  };

  const setField = (key, val, type) => setForm((f) => ({ ...f, [key]: type === "number" ? Number(val) : val }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.tags && typeof payload.tags === "string") {
        payload.tags = payload.tags.split(",").map((t) => t.trim()).filter(Boolean);
      }
      if (editing === "new") await api.post(`/admin/${name}`, payload);
      else await api.put(`/admin/${name}/${editing}`, payload);
      toast.success("Saved");
      await refresh();
      close();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${name}/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={`editor-${name}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
        <button onClick={openNew} data-testid={`add-${name}`} className="inline-flex items-center gap-2 bg-[#D4FF00] text-black text-sm font-semibold px-4 py-2">
          <Plus size={16} /> Add
        </button>
      </div>

      <div className="space-y-2">
        {(items || []).map((item) => (
          <div key={item.id} className="border border-white/10 bg-[#0e0e0e]">
            {editing === item.id ? (
              <EditForm fields={fields} form={form} setField={setField} onSave={save} onCancel={close} busy={busy} />
            ) : (
              <div className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{item[fields[0].key] || "Untitled"}</p>
                  <p className="text-xs text-zinc-500 truncate">{item[fields[1]?.key] || ""}</p>
                </div>
                <div className="flex gap-2 shrink-0 ml-4">
                  <button onClick={() => openEdit(item)} data-testid={`edit-${name}-${item.id}`} className="text-xs border border-white/20 px-3 py-1.5 hover:border-white">Edit</button>
                  <button onClick={() => remove(item.id)} data-testid={`delete-${name}-${item.id}`} className="text-xs border border-[#FF3366]/40 text-[#FF3366] px-2.5 py-1.5 hover:border-[#FF3366]"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}

        {editing === "new" && (
          <div className="border border-[#D4FF00]/40 bg-[#0e0e0e]">
            <EditForm fields={fields} form={form} setField={setField} onSave={save} onCancel={close} busy={busy} />
          </div>
        )}
      </div>
    </div>
  );
}

function EditForm({ fields, form, setField, onSave, onCancel, busy }) {
  return (
    <div className="p-4 space-y-4">
      {fields.map((f) => (
        <div key={f.key}>
          <label className={label}>{f.label}</label>
          {f.type === "textarea" ? (
            <textarea rows={3} className={input} value={form[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value, f.type)} data-testid={`field-${f.key}`} />
          ) : (
            <input type={f.type === "number" ? "number" : "text"} className={input} value={Array.isArray(form[f.key]) ? form[f.key].join(", ") : (form[f.key] ?? "")} onChange={(e) => setField(f.key, e.target.value, f.type)} data-testid={`field-${f.key}`} />
          )}
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <button onClick={onSave} disabled={busy} className="inline-flex items-center gap-2 bg-[#D4FF00] text-black text-sm font-semibold px-4 py-2 disabled:opacity-50" data-testid="save-item"><Save size={15} /> {busy ? "Saving…" : "Save"}</button>
        <button onClick={onCancel} className="inline-flex items-center gap-2 border border-white/20 text-sm px-4 py-2"><X size={15} /> Cancel</button>
      </div>
    </div>
  );
}
